import {
  collectStoredJwtTokens,
  isJwt,
  isStoredJwtSyncHeader,
} from './credential-auth-material.utils';
import { getJwtExpirationSeconds } from './encoding-decode.utils';

export type AuthMaterialExpirySource =
  | 'jwt'
  | 'cookie'
  | 'cache_ttl'
  | 'unknown';

export type AuthMaterialExpiryStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'unknown';

export type AuthMaterialExpiry = {
  expiresAt: string | null;
  source: AuthMaterialExpirySource;
  status: AuthMaterialExpiryStatus;
  computedAt: string;
};

export type AuthMaterialArtifactKind =
  | 'header'
  | 'cookie'
  | 'secret'
  | 'localStorage'
  | 'sessionStorage';

export type AuthMaterialForExpiry = {
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string; expires?: string | null }>;
  secrets: Array<{ key: string; value: string }>;
  cacheExpiresAt?: Date | string | null;
  fullBrowserStorage?: {
    localStorage: Array<{ key: string; value: string }>;
    sessionStorage: Array<{ key: string; value: string }>;
  };
};

export type ArtifactExpiryInput = {
  kind: AuthMaterialArtifactKind;
  name: string;
  value: string;
  expires?: string | null;
};

const EXPIRING_SOON_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const MASKED_VALUE_PREVIEW = '••••••••';

function decodeJwtExpSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (!isJwt(trimmed)) {
    return null;
  }

  return getJwtExpirationSeconds(trimmed);
}

function stripAuthScheme(value: string): string {
  const match = value.match(/^[A-Za-z][A-Za-z0-9+.-]*\s+(.+)$/);
  return match ? match[1].trim() : value.trim();
}

function parseExpiresToDate(expires: string): Date | null {
  const trimmed = expires.trim();
  if (!trimmed) {
    return null;
  }

  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric) && trimmed === String(numeric)) {
    const millis = numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
    const parsedNumeric = new Date(millis);
    return Number.isNaN(parsedNumeric.getTime()) ? null : parsedNumeric;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveExpiryFromValue({
  value,
  expires,
}: {
  value: string;
  expires?: string | null;
}): { expiresAt: Date; source: AuthMaterialExpirySource } | null {
  const jwtExpSeconds = decodeJwtExpSeconds(stripAuthScheme(value));
  if (jwtExpSeconds != null) {
    return {
      expiresAt: new Date(jwtExpSeconds * 1000),
      source: 'jwt',
    };
  }

  if (expires) {
    const cookieExpiry = parseExpiresToDate(expires);
    if (cookieExpiry) {
      return {
        expiresAt: cookieExpiry,
        source: 'cookie',
      };
    }
  }

  return null;
}

function resolveStatus({
  expiresAt,
  now,
}: {
  expiresAt: Date;
  now: Date;
}): AuthMaterialExpiryStatus {
  if (expiresAt.getTime() <= now.getTime()) {
    return 'expired';
  }

  if (expiresAt.getTime() - now.getTime() <= EXPIRING_SOON_THRESHOLD_MS) {
    return 'expiring_soon';
  }

  return 'valid';
}

function collectCandidateExpiries({
  material,
  now,
}: {
  material: AuthMaterialForExpiry;
  now: Date;
}): Array<{ expiresAt: Date; source: AuthMaterialExpirySource }> {
  const candidates: Array<{
    expiresAt: Date;
    source: AuthMaterialExpirySource;
  }> = [];

  for (const header of material.headers) {
    const resolved = resolveExpiryFromValue({ value: header.value });
    if (resolved) {
      candidates.push(resolved);
    }
  }

  for (const cookie of material.cookies) {
    const resolved = resolveExpiryFromValue({
      value: cookie.value,
      expires: cookie.expires,
    });
    if (resolved) {
      candidates.push(resolved);
    }
  }

  for (const secret of material.secrets) {
    const resolved = resolveExpiryFromValue({ value: secret.value });
    if (resolved) {
      candidates.push(resolved);
    }
  }

  for (const item of material.fullBrowserStorage?.localStorage ?? []) {
    const resolved = resolveExpiryFromValue({ value: item.value });
    if (resolved) {
      candidates.push(resolved);
    }
  }

  for (const item of material.fullBrowserStorage?.sessionStorage ?? []) {
    const resolved = resolveExpiryFromValue({ value: item.value });
    if (resolved) {
      candidates.push(resolved);
    }
  }

  if (material.cacheExpiresAt) {
    const cacheExpiry = new Date(material.cacheExpiresAt);
    if (
      !Number.isNaN(cacheExpiry.getTime()) &&
      cacheExpiry.getTime() > now.getTime()
    ) {
      candidates.push({
        expiresAt: cacheExpiry,
        source: 'cache_ttl',
      });
    }
  }

  return candidates;
}

export function extractArtifactExpiry({
  value,
  expires,
}: {
  value: string;
  expires?: string | null;
}): string | null {
  const resolved = resolveExpiryFromValue({ value, expires });
  return resolved ? resolved.expiresAt.toISOString() : null;
}

export function extractAuthMaterialExpiry(
  material: AuthMaterialForExpiry,
  now: Date = new Date(),
): AuthMaterialExpiry {
  const candidates = collectCandidateExpiries({ material, now });

  if (candidates.length === 0) {
    return {
      expiresAt: null,
      source: 'unknown',
      status: 'unknown',
      computedAt: now.toISOString(),
    };
  }

  const earliest = candidates.reduce((current, candidate) =>
    candidate.expiresAt.getTime() < current.expiresAt.getTime()
      ? candidate
      : current,
  );

  return {
    expiresAt: earliest.expiresAt.toISOString(),
    source: earliest.source,
    status: resolveStatus({ expiresAt: earliest.expiresAt, now }),
    computedAt: now.toISOString(),
  };
}

export function buildMaskedValuePreview(): string {
  return MASKED_VALUE_PREVIEW;
}

export function mapArtifactsToSummary({
  headers,
  cookies,
  secrets,
  fullBrowserStorage,
}: {
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string; expires?: string | null }>;
  secrets: Array<{ key: string; value: string }>;
  fullBrowserStorage?: {
    localStorage: Array<{ key: string; value: string }>;
    sessionStorage: Array<{ key: string; value: string }>;
  };
}): Array<{
  kind: AuthMaterialArtifactKind;
  name: string;
  valuePreview: string;
  expiresAt: string | null;
}> {
  const maskedPreview = buildMaskedValuePreview();
  const artifacts: Array<{
    kind: AuthMaterialArtifactKind;
    name: string;
    valuePreview: string;
    expiresAt: string | null;
  }> = [];

  for (const header of headers) {
    artifacts.push({
      kind: 'header',
      name: header.key,
      valuePreview: maskedPreview,
      expiresAt: extractArtifactExpiry({ value: header.value }),
    });
  }

  for (const cookie of cookies) {
    artifacts.push({
      kind: 'cookie',
      name: cookie.name,
      valuePreview: maskedPreview,
      expiresAt: extractArtifactExpiry({
        value: cookie.value,
        expires: cookie.expires,
      }),
    });
  }

  for (const secret of secrets) {
    artifacts.push({
      kind: 'secret',
      name: secret.key,
      valuePreview: maskedPreview,
      expiresAt: extractArtifactExpiry({ value: secret.value }),
    });
  }

  for (const item of fullBrowserStorage?.localStorage ?? []) {
    artifacts.push({
      kind: 'localStorage',
      name: item.key,
      valuePreview: maskedPreview,
      expiresAt: extractArtifactExpiry({ value: item.value }),
    });
  }

  for (const item of fullBrowserStorage?.sessionStorage ?? []) {
    artifacts.push({
      kind: 'sessionStorage',
      name: item.key,
      valuePreview: maskedPreview,
      expiresAt: extractArtifactExpiry({ value: item.value }),
    });
  }

  return artifacts;
}

export function mapRevealedArtifactsFromCredential({
  headers,
  cookies,
  secrets,
  fullBrowserStorage,
}: {
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string; expires?: string | null }>;
  secrets: Array<{ key: string; value: string }>;
  fullBrowserStorage?: {
    localStorage: Array<{ key: string; value: string }>;
    sessionStorage: Array<{ key: string; value: string }>;
  };
}): Array<{
  kind: AuthMaterialArtifactKind;
  name: string;
  valuePreview: string;
  value: string;
  expiresAt: string | null;
}> {
  const maskedPreview = buildMaskedValuePreview();

  const toArtifact = ({
    kind,
    name,
    value,
    expires,
  }: {
    kind: AuthMaterialArtifactKind;
    name: string;
    value: string;
    expires?: string | null;
  }) => ({
    kind,
    name,
    valuePreview: maskedPreview,
    value,
    expiresAt: extractArtifactExpiry({ value, expires }),
  });

  return [
    ...headers.map((header) =>
      toArtifact({ kind: 'header', name: header.key, value: header.value }),
    ),
    ...cookies.map((cookie) =>
      toArtifact({
        kind: 'cookie',
        name: cookie.name,
        value: cookie.value,
        expires: cookie.expires,
      }),
    ),
    ...secrets.map((secret) =>
      toArtifact({ kind: 'secret', name: secret.key, value: secret.value }),
    ),
    ...(fullBrowserStorage?.localStorage ?? []).map((item) =>
      toArtifact({
        kind: 'localStorage',
        name: item.key,
        value: item.value,
      }),
    ),
    ...(fullBrowserStorage?.sessionStorage ?? []).map((item) =>
      toArtifact({
        kind: 'sessionStorage',
        name: item.key,
        value: item.value,
      }),
    ),
  ];
}

export type CredentialAuthStorageItem = { key: string; value: string };

export type CredentialAuthCookie = { name: string; value: string };

export type CredentialAuthStorageContext = {
  headers: CredentialAuthStorageItem[];
  cookies: CredentialAuthCookie[];
  secrets: CredentialAuthStorageItem[];
  fullBrowserStorage?: {
    localStorage: CredentialAuthStorageItem[];
    sessionStorage: CredentialAuthStorageItem[];
  };
};

const STORAGE_AUTH_TOKEN_KEYS: readonly string[] = [
  'access_token',
  'accesstoken',
  'access-token',
  'token',
  'id_token',
  'idtoken',
];

const SESSION_AUTH_COOKIE_NAMES: readonly string[] = [
  'session',
  'session_id',
  'sessionid',
  'jsessionid',
  'connect.sid',
  'auth',
  'authtoken',
  'access_token',
  'id_token',
  'sid',
  'token',
  'refreshtoken',
  'refresh_token',
];

function storageHasAuthMaterial(items: CredentialAuthStorageItem[]): boolean {
  return items.some((item) => {
    const lowerKey = item.key.toLowerCase();
    return (
      isJwt(item.value) ||
      STORAGE_AUTH_TOKEN_KEYS.includes(lowerKey) ||
      SESSION_AUTH_COOKIE_NAMES.includes(lowerKey)
    );
  });
}

function cookiesIncludeAuthMaterial(cookies: CredentialAuthCookie[]): boolean {
  return cookies.some((cookie) => {
    const lowerName = cookie.name.toLowerCase();
    return isJwt(cookie.value) || SESSION_AUTH_COOKIE_NAMES.includes(lowerName);
  });
}

export function getCredentialReadiness({
  headers,
  cookies,
  secrets,
  fullBrowserStorage,
}: {
  headers: Array<unknown>;
  cookies: Array<CredentialAuthCookie>;
  secrets: Array<unknown>;
  fullBrowserStorage?: {
    localStorage: Array<unknown>;
    sessionStorage: Array<unknown>;
  };
}): 'ready' | 'reverify' {
  const hasFullBrowserStorage =
    (fullBrowserStorage?.localStorage.length ?? 0) > 0 ||
    (fullBrowserStorage?.sessionStorage.length ?? 0) > 0;

  if (hasFullBrowserStorage) {
    return 'ready';
  }

  if (cookiesIncludeAuthMaterial(cookies)) {
    return 'ready';
  }

  if (headers.length > 0 || cookies.length > 0 || secrets.length > 0) {
    return 'reverify';
  }

  return 'reverify';
}

export function shouldPreferSessionMaterialOverTokenHeaders({
  headers,
  cookies,
  secrets,
  fullBrowserStorage,
}: CredentialAuthStorageContext): boolean {
  const readiness = getCredentialReadiness({
    headers,
    cookies,
    secrets,
    fullBrowserStorage,
  });

  if (readiness !== 'ready') {
    return false;
  }

  return (
    storageHasAuthMaterial(fullBrowserStorage?.localStorage ?? []) ||
    storageHasAuthMaterial(fullBrowserStorage?.sessionStorage ?? []) ||
    cookiesIncludeAuthMaterial(cookies)
  );
}

export function shouldApplyDerivedBearerAuthFromCredentialMaterial(
  context: CredentialAuthStorageContext,
): boolean {
  return !shouldPreferSessionMaterialOverTokenHeaders(context);
}

export function filterCredentialHeadersForSessionBasedAuth({
  headers,
  ...context
}: CredentialAuthStorageContext): CredentialAuthStorageItem[] {
  if (!shouldPreferSessionMaterialOverTokenHeaders({ headers, ...context })) {
    return headers;
  }

  const storedJwtTokens = collectStoredJwtTokens({
    secrets: context.secrets,
    cookies: context.cookies,
    fullBrowserStorage: context.fullBrowserStorage,
  });

  return headers.filter((header) =>
    isStoredJwtSyncHeader({ header, storedJwtTokens }),
  );
}
